import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { JobCard } from "../components/JobCard";
import { JobCoverImage } from "../components/JobCoverImage";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { MapPin, Clock, Euro, Eye, Heart, Share2, Lock, Send, Building2, ExternalLink, Crown, Shield, Zap } from "lucide-react";
import { useMemo } from "react";
import { useSEO, jobPostingJsonLd } from "../lib/seo";

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);

  const seo = useMemo(() => {
    const job = data?.job;
    if (!job) return {};
    return {
      title: `${job.title} — ${job.company_name} | ${job.concelho} | FIRST2JOB`,
      description: `${job.title} em ${job.concelho}, ${job.district}. ${job.regime}, ${job.work_mode}${job.salary_min ? `, ${job.salary_min}€–${job.salary_max}€/mês` : ""}. Candidata-te no FIRST2JOB.`,
      jsonLd: jobPostingJsonLd(job),
    };
  }, [data]);
  useSEO(seo);

  useEffect(() => {
    api.get(`/jobs/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (user && user.role === "candidate" && data?.job) {
      api.get("/candidate/saved").then((r) => {
        if (r.data.job_ids.includes(data.job.id)) setSaved(true);
      }).catch(() => {});
      api.get("/candidate/applications").then((r) => {
        if (r.data.applications.some((a) => a.job_id === data.job.id)) setApplied(true);
      }).catch(() => {});
    }
  }, [user, data]);

  if (data === false) return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /><p className="text-center py-32 text-slate-500">Vaga não encontrada.</p></div>;
  if (!data) return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /><p className="text-center py-32 text-slate-500">A carregar...</p></div>;

  const { job, similar } = data;
  const isCandidate = user && user.role === "candidate";
  const isGuest = user === false;
  const authLoading = user === null;

  const requireCandidate = (action) => {
    if (isGuest) {
      toast.info("Cria uma conta de candidato para " + action + ".");
      navigate(`/auth?tab=register&next=/empregos/${slug}`);
      return false;
    }
    if (!isCandidate) {
      toast.error("Apenas contas de candidato podem " + action + ".");
      return false;
    }
    return true;
  };

  const submitApplication = async () => {
    try {
      await api.post(`/jobs/${job.id}/apply`, { message });
      setApplied(true);
      setApplyOpen(false);
      toast.success("Candidatura enviada! Acompanha o estado no teu painel.");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const toggleSave = async () => {
    if (!requireCandidate("guardar vagas")) return;
    try {
      const { data: r } = await api.post(`/jobs/${job.id}/save`);
      setSaved(r.saved);
      toast.success(r.saved ? "Vaga guardada" : "Vaga removida dos favoritos");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copiado para partilhar");
  };

  const tier = job.boost?.tier;
  const isExternal = job.source_type && job.source_type !== "internal";
  const sourceLabel = job.source_label || (job.source_type === "iefp" ? "IEFP" : job.source_type);
  const expired = !!job.expired;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
          <div className="relative h-48 sm:h-64 bg-slate-100">
            <JobCoverImage job={job} className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-950/40 to-transparent pointer-events-none" aria-hidden="true" />
          </div>
          <div className="p-6 sm:p-10">
          {expired && (
            <div data-testid="job-expired-banner" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-bold text-amber-800 text-sm">Esta vaga já não está ativa</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {isExternal
                    ? `A oferta pode já não estar disponível, mas podes confirmar diretamente em ${sourceLabel}.`
                    : "Esta vaga foi encerrada e já não aceita novas candidaturas. Explora outras vagas semelhantes abaixo."}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                {job.company_slug ? (
                  <Link data-testid="job-company-link" to={`/empresas/${job.company_slug}`} className="hover:text-slate-900 hover:underline transition-colors">{job.company_name}</Link>
                ) : job.company_name}
              </p>
              <h1 data-testid="job-title" className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-slate-900 mt-2">{job.title}</h1>
            </div>
            {(tier === "gold" || tier === "destaque_max") && <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 font-black border-0"><Crown className="w-3 h-3 mr-1" />PREMIUM GOLD</Badge>}
            {(tier === "silver" || tier === "destaque") && <Badge className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold border-0"><Shield className="w-3 h-3 mr-1" />SILVER</Badge>}
            {tier === "express" && <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold border-0"><Zap className="w-3 h-3 mr-1" />EXPRESS</Badge>}
            {isExternal && <Badge data-testid="iefp-source-badge" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-bold">Fonte: {sourceLabel}</Badge>}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600 mt-5">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{job.concelho}, {job.district}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{job.regime} · {job.work_mode}</span>
            {job.salary_min && <span className="flex items-center gap-1.5"><Euro className="w-4 h-4" />{job.salary_min}€ – {job.salary_max}€ / mês</span>}
            <span className="flex items-center gap-1.5"><Eye className="w-4 h-4" />{job.views} visualizações</span>
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            {isExternal ? (
              <a data-testid="external-apply-btn" href={job.apply_url} target="_blank" rel="noreferrer">
                <Button className="rounded-full bg-slate-900 hover:bg-slate-800 px-8">
                  <ExternalLink className="w-4 h-4 mr-2" /> {expired ? `Ver em ${sourceLabel}` : `Candidatar em ${sourceLabel}`}
                </Button>
              </a>
            ) : expired ? (
              <Button data-testid="expired-apply-btn" disabled className="rounded-full bg-slate-400">
                Vaga encerrada
              </Button>
            ) : applied ? (
              <Button data-testid="applied-badge-btn" disabled className="rounded-full bg-emerald-600">Candidatura enviada ✓</Button>
            ) : (
              <Button data-testid="apply-btn" disabled={authLoading} className="rounded-full bg-slate-900 hover:bg-slate-800 px-8"
                onClick={() => { if (requireCandidate("te candidatares")) setApplyOpen(true); }}>
                {isGuest ? <Lock className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Candidatar-me
              </Button>
            )}
            <Button data-testid="save-btn" disabled={authLoading} variant="outline" className="rounded-full" onClick={toggleSave}>
              <Heart className={`w-4 h-4 mr-2 ${saved ? "fill-red-500 text-red-500" : ""}`} /> {saved ? "Guardada" : "Guardar"}
            </Button>
            <Button data-testid="share-btn" variant="outline" className="rounded-full" onClick={share}>
              <Share2 className="w-4 h-4 mr-2" /> Partilhar
            </Button>
          </div>
          {isGuest && !isExternal && (
            <p data-testid="content-lock-note" className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Para te candidatares ou guardares esta vaga, cria uma conta gratuita.
            </p>
          )}
          {isExternal && (
            <p data-testid="iefp-external-note" className="text-xs text-slate-500 mt-3">
              Oferta importada de {sourceLabel} — a candidatura é feita diretamente no site original.
            </p>
          )}

          <div className="mt-10 space-y-8">
            <div>
              <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Descrição</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p>
            </div>
            {job.requirements && (
              <div>
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Requisitos</h2>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.requirements}</p>
              </div>
            )}
          </div>
          </div>
        </div>

        {similar.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-5">Vagas semelhantes</h2>
            <div className="space-y-4">{similar.map((j) => <JobCard key={j.id} job={j} />)}</div>
          </div>
        )}
      </div>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent data-testid="apply-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tighter">Candidatar a {job.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Mensagem para a empresa (opcional):</p>
          <Textarea data-testid="apply-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Apresenta-te em poucas linhas..." rows={4} />
          <Button data-testid="apply-submit-btn" onClick={submitApplication} className="rounded-full bg-slate-900 hover:bg-slate-800">
            Enviar candidatura
          </Button>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
